import { useMemo, useCallback } from "react";
import { sortByDate } from "../../../utils/dateResolvers";
import { calculateStatsFromCustomers } from "../../../utils/orderUtils";

export const useOrderGrouping = (filteredCustomers: any[]) => {
  const groupedCustomers = useMemo(() => {
    const groups = filteredCustomers.reduce((acc: any, customer: any) => {
      const key = customer._id;
      acc[key] = {
        facet: customer,
        customerId: customer._id,
        customerName: customer.name,
      };
      return acc;
    }, {} as Record<string, { facet: any; customerId: string; customerName: string }>);

    const sortedGroups: Record<
      string,
      { facet: any; customerId: string; customerName: string }
    > = {};
    Object.keys(groups)
      .sort((a, b) => {
        const etaA = groups[a].facet?.minETAdate;
        const etaB = groups[b].facet?.minETAdate;

        return sortByDate(
          groups[a],
          groups[b],
          () => etaA,
          () => etaB,
          (a, b) => {
            const countA = a.facet?.count || 0;
            const countB = b.facet?.count || 0;
            return countB - countA;
          }
        );
      })
      .forEach((key) => {
        sortedGroups[key] = groups[key];
      });

    return sortedGroups;
  }, [filteredCustomers]);

  const getCustomerRecordByName = useCallback(
    (customerName: string) => {
      return Object.values(groupedCustomers).find(
        (customer) => customer.customerName === customerName
      );
    },
    [groupedCustomers]
  );

  const getCustomerRecordById = useCallback(
    (customerId: string) => {
      return groupedCustomers[customerId];
    },
    [groupedCustomers]
  );

  const getTotalStats = useCallback(() => {
    const customerFacets = Object.values(groupedCustomers).map(
      (group) => group.facet
    );
    return calculateStatsFromCustomers(customerFacets);
  }, [groupedCustomers]);

  return {
    groupedCustomers,
    getCustomerRecordByName,
    getCustomerRecordById,
    getTotalStats,
  };
};
